import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { BlockSessionSummary, BlockVisibility, ShowType } from '@fetmode/types';
import { BLOCK_VISIBILITIES, SHOW_TYPES } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

type SessionDraft = {
  durationMinutes: string;
  priceCoins: string;
  showType: ShowType;
  visibility: BlockVisibility;
  discountPct: string;
  scheduledAt: string;
};

const emptyDraft = (): SessionDraft => ({
  durationMinutes: '60',
  priceCoins: '500',
  showType: 'EXCLUSIVE',
  visibility: 'PUBLIC',
  discountPct: '0',
  scheduledAt: '',
});

export function ModelBlockSessionsCard(): ReactElement {
  const { api } = useAuth();
  const [sessions, setSessions] = useState<BlockSessionSummary[]>([]);
  const [draft, setDraft] = useState<SessionDraft>(emptyDraft);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const result = await api.listMyBlockSessions();
    setSessions(result.sessions);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.blockSessions.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function handleAdd(): Promise<void> {
    const durationMinutes = Number.parseInt(draft.durationMinutes, 10);
    const priceCoins = Number.parseInt(draft.priceCoins, 10);
    const discountPct = Number.parseInt(draft.discountPct, 10);
    if (
      !Number.isFinite(durationMinutes) ||
      durationMinutes < 1 ||
      !Number.isFinite(priceCoins) ||
      priceCoins < 1 ||
      !Number.isFinite(discountPct) ||
      discountPct < 0 ||
      discountPct > 90
    ) {
      setError(t('common.studio.blockSessions.invalidItem'));
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await api.createBlockSession({
        durationMinutes,
        priceCoins,
        showType: draft.showType,
        visibility: draft.visibility,
        discountPct,
        scheduledAt: draft.scheduledAt.trim() ? new Date(draft.scheduledAt).toISOString() : null,
      });
      setDraft(emptyDraft());
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.blockSessions.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleCancel(sessionId: string): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.updateBlockSession(sessionId, { status: 'CANCELLED' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.blockSessions.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(sessionId: string): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.deleteBlockSession(sessionId);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.blockSessions.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.blockSessions.loading')}</p>
    );
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.blockSessions.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.blockSessions.hint')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
        {sessions.map((session) => (
          <li
            key={session.id}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              alignItems: 'center',
              padding: '0.5rem 0',
              borderBottom: '1px solid var(--fm-color-border)',
            }}
          >
            <span style={{ flex: '1 1 12rem', fontWeight: 600, fontSize: '0.875rem' }}>
              {session.showType} · {session.durationMinutes}m · {session.priceCoins} ·{' '}
              {session.status}
              {session.scheduledAt
                ? ` · ${new Date(session.scheduledAt).toLocaleString()}`
                : ''}
            </span>
            {session.status === 'OPEN' ? (
              <Button
                label={t('common.studio.blockSessions.cancel')}
                variant="ghost"
                disabled={isSaving}
                onClick={() => {
                  void handleCancel(session.id);
                }}
              />
            ) : null}
            <Button
              label={t('common.studio.blockSessions.delete')}
              variant="ghost"
              disabled={isSaving}
              onClick={() => {
                void handleDelete(session.id);
              }}
            />
          </li>
        ))}
      </ul>

      {sessions.length === 0 ? (
        <p style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
          {t('common.studio.blockSessions.empty')}
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
        <label>
          {t('common.studio.blockSessions.showType')}
          <select
            className="fm-input"
            value={draft.showType}
            onChange={(event) => {
              setDraft({ ...draft, showType: event.target.value as ShowType });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          >
            {SHOW_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('common.studio.blockSessions.duration')}
          <input
            className="fm-input"
            type="number"
            min={1}
            value={draft.durationMinutes}
            onChange={(event) => {
              setDraft({ ...draft, durationMinutes: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.blockSessions.price')}
          <input
            className="fm-input"
            type="number"
            min={1}
            value={draft.priceCoins}
            onChange={(event) => {
              setDraft({ ...draft, priceCoins: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.blockSessions.visibility')}
          <select
            className="fm-input"
            value={draft.visibility}
            onChange={(event) => {
              setDraft({ ...draft, visibility: event.target.value as BlockVisibility });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          >
            {BLOCK_VISIBILITIES.map((visibility) => (
              <option key={visibility} value={visibility}>
                {visibility}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('common.studio.blockSessions.discount')}
          <input
            className="fm-input"
            type="number"
            min={0}
            max={90}
            value={draft.discountPct}
            onChange={(event) => {
              setDraft({ ...draft, discountPct: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.blockSessions.scheduledAt')}
          <input
            className="fm-input"
            type="datetime-local"
            value={draft.scheduledAt}
            onChange={(event) => {
              setDraft({ ...draft, scheduledAt: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <Button
          label={
            isSaving ? t('common.studio.blockSessions.saving') : t('common.studio.blockSessions.add')
          }
          disabled={isSaving}
          onClick={() => {
            void handleAdd();
          }}
        />
      </div>
    </div>
  );
}
